import { WaterMeterChangeModel } from "@/core/models/water-meter-change.model";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import {
    WaterMeterChangeReason,
    WaterMeterChangeStatus,
    getWaterMeterChangeReasonDisplay,
    getWaterMeterChangeStatusDisplay,
} from "@/core/enums/global.enums";
import { WaterMeterChangeRepo } from "@/core/repos/tables/water-meter-change.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import container from "@/lib/di/container";
import dayjs from "dayjs";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { FilterInputType, FilterInputTemplate } from "@/app/home/components/filter/FilterBar";
import { ReportTableConfigs } from "@/app/home/components/report/ReportTable";

const waterMeterChangeRepo = container.get(WaterMeterChangeRepo);
const tollAreaRepo = container.get(TollAreaRepo);

const REASON_OPTIONS = (Object.keys(WaterMeterChangeReason) as (keyof typeof WaterMeterChangeReason)[]).map((key) => ({
    name: getWaterMeterChangeReasonDisplay(WaterMeterChangeReason[key]),
    value: WaterMeterChangeReason[key],
}));

const STATUS_OPTIONS = (Object.keys(WaterMeterChangeStatus) as (keyof typeof WaterMeterChangeStatus)[]).map((key) => ({
    name: getWaterMeterChangeStatusDisplay(WaterMeterChangeStatus[key]),
    value: WaterMeterChangeStatus[key],
}));

export const WATER_METER_CHANGE_REPORT_CONFIGS: {
    name: string,
    props: ReportTableConfigs<WaterMeterChangeModel, null>,
} = {
    name: "Danh sách đồng hồ được thay",
    props: {
        initialFilterMap: new Map(
            Object.entries({ ...GlobalUtils.getPreviousMonthFilter() })
        ),
        columns: [
            { dataIndex: "waterUserCode", name: "Mã hợp đồng" },
            { dataIndex: "waterUserName", name: "Tên khách hàng" },
            { dataIndex: "address", name: "Địa chỉ" },
            { dataIndex: "phone", name: "Điện thoại" },
            { dataIndex: "tollAreaName", name: "Khu vực" },
            { dataIndex: "oldWaterMeterCode", name: "Mã đồng hồ cũ" },
            { dataIndex: "removedIndex", name: "Chỉ số tháo" },
            { dataIndex: "newWaterMeterCode", name: "Mã đồng hồ mới" },
            { dataIndex: "headIndex", name: "Chỉ số đầu" },
            {
                name: "Ngày thay",
                pipe: (value: string) => (value ? formatDateTimePipe(value) : ""),
                dataIndex: "changeDate",
            },
            {
                name: "Kỳ (T/N)",
                pipe: (value: WaterMeterChangeModel) => `${value.month}/${value.year}`,
            },
            {
                dataIndex: "reason",
                name: "Lý do",
                pipe: (value: string) => getWaterMeterChangeReasonDisplay(value) || value,
            },
            {
                dataIndex: "status",
                name: "Trạng thái",
                pipe: (value: string) => getWaterMeterChangeStatusDisplay(value) || value,
            },
            { dataIndex: "note", name: "Ghi chú" },
            { dataIndex: "createdBy", name: "Người tạo" },
            {
                dataIndex: "createdAt",
                name: "Ngày tạo",
                pipe: (value: string) => (value ? formatDateTimePipe(value) : ""),
            },
            { dataIndex: "confirmedBy", name: "Người xác nhận" },
            {
                dataIndex: "confirmedAt",
                name: "Ngày xác nhận",
                pipe: (value: string) => (value ? formatDateTimePipe(value) : ""),
            },
        ],
        filterTemplates: [
            {
                dataIndex: "search",
                label: "Tìm kiếm",
                placeholder: "Mã HĐ / Mã đồng hồ / Tên khách hàng",
                type: FilterInputType.text,
            },
            {
                dataIndex: "month",
                label: "Kỳ",
                placeholder: "Chọn kỳ",
                type: FilterInputType.month,
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "reason",
                label: "Lý do",
                placeholder: "Chọn lý do thay",
                type: FilterInputType.select,
                options: REASON_OPTIONS,
            },
            {
                dataIndex: "status",
                label: "Trạng thái",
                placeholder: "Chọn trạng thái",
                type: FilterInputType.select,
                options: STATUS_OPTIONS,
            },
            {
                type: FilterInputType.dateRange,
                label: "Ngày thay",
                placeholder: "Từ ngày",
                secondaryPlaceholder: "Đến ngày",
                rangeIndex: { from: "fromDate", to: "toDate" },
            },
        ],
        handleExport: async (filterMap, defaultFilter) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const query: any = { ...defaultFilter };
            filterMap.forEach((filter) => {
                if (filter.value) {
                    query[filter.key] = filter.value;
                }
            });
            const blobData = await waterMeterChangeRepo.exportChangesExcel(query);
            if (blobData) {
                GlobalUtils.downloadXLSX(
                    blobData,
                    `DanhSachDongHoDuocThay-${dayjs().format("DDMMYYYY-hhmm")}`
                );
            }
        },
        init: async (filterTemplates) => {
            const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({
                name: area.name,
                value: area.id,
            }));
            const templates: FilterInputTemplate[] = [...filterTemplates];
            const tollAreaTemplate = templates.find(
                (template) => template.dataIndex === "tollAreaId"
            );
            if (tollAreaTemplate) {
                tollAreaTemplate.options = tollAreas;
            }
            return {
                filterTemplates: templates,
            };
        },
        onFilterChanged: async () => {
            return {
                filterTemplates: undefined,
            };
        },
        repository: waterMeterChangeRepo,
    },
};
